import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, InstagramIcon, MapPinIcon, CheckCircle2Icon, AlertCircleIcon, InfoIcon, SendIcon, LoaderIcon } from 'lucide-react';
type FormFields = {
  name: string;
  email: string;
  phone: string;
  message: string;
};
type FormErrors = Partial<Record<keyof FormFields, string>>;
type Status = 'idle' | 'submitting' | 'success' | 'error';
const EMPTY_FORM: FormFields = {
  name: '',
  email: '',
  phone: '',
  message: ''
};
export function Contact() {
  const [fields, setFields] = useState<FormFields>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<Status>('idle');
  const handleChange = (key: keyof FormFields, value: string) => {
    setFields(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => {
      if (!prev[key]) return prev;
      const next = {
        ...prev
      };
      delete next[key];
      return next;
    });
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate(fields);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStatus('idle');
      return;
    }
    setStatus('submitting');
    try {
      await new Promise(resolve => setTimeout(resolve, 900));
      setStatus('success');
      setFields(EMPTY_FORM);
    } catch {
      setStatus('error');
    }
  };
  const isSubmitting = status === 'submitting';
  return <section id="contact" className="py-24 bg-offwhite-100" data-id="element-154" data-mp="364zg0qvboelx">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-155" data-mp="2s4onljed6ryn">
        <div className="grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-12 lg:gap-16 items-start" data-id="element-156" data-mp="2s682b5kdguyb">
          {/* Intro / direct contact */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-157" data-mp="qcinmqmsvj0a">
            <h2 className="text-4xl md:text-5xl font-bold text-charcoal-950 mb-5" data-id="element-158" data-mp="1ubxd44ls87f0">
              Have a Question?
            </h2>
            <p className="text-xl text-charcoal-800 leading-relaxed mb-6" data-id="element-159" data-mp="1pdnhxu0256te">
              Not sure if this is the right fit, or just want to understand your
              options? Send me a message and I'll get back to you personally.
            </p>

            <div className="flex gap-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5 mb-10" data-id="element-160" data-mp="2d98a1ijxyora">
              <InfoIcon size={22} className="text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-161" data-mp="28bglmyii9kru" />
              <p className="text-charcoal-800 leading-relaxed" data-id="element-162" data-mp="35rvpoig65bkz">
                I'm happy to answer general questions about what you're dealing
                with and which care options might make sense.
              </p>
            </div>

            <h3 className="text-sm font-bold uppercase tracking-widest text-charcoal-500 mb-5" data-id="element-163" data-mp="3damxr0n5o5v9">
              Reach me directly
            </h3>
            <ul className="space-y-5" data-id="element-164" data-mp="3iarcal4i6qiu">
              <li className="flex items-center gap-4" data-id="element-165" data-mp="3marvej2jrpw8">
                <span className="bg-white w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border border-offwhite-200" data-id="element-166" data-mp="3m85mog3x74cf">
                  <MailIcon size={20} className="text-amber-500" aria-hidden="true" data-id="element-167" data-mp="2idt8yvkqgw" />
                </span>
                <a href="mailto:info@mcleanphysicaltherapy.com" className="text-charcoal-950 font-medium hover:text-amber-600 transition-colors break-all" data-id="element-168" data-mp="4tidglzdpmn3">
                  info@mcleanphysicaltherapy.com
                </a>
              </li>
              <li className="flex items-center gap-4" data-id="element-169" data-mp="isru1mrhyirl">
                <span className="bg-white w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border border-offwhite-200" data-id="element-170" data-mp="ir6xxspjarrm">
                  <InstagramIcon size={20} className="text-amber-500" aria-hidden="true" data-id="element-171" data-mp="160ragkuorxw" />
                </span>
                <a href="https://www.instagram.com/dylanmclean.dpt/" target="_blank" rel="noopener noreferrer" className="text-charcoal-950 font-medium hover:text-amber-600 transition-colors" data-id="element-172" data-mp="9x1295d5pj6x">
                  @dylanmclean.dpt
                </a>
              </li>
              <li className="flex items-center gap-4" data-id="element-173" data-mp="3sctol9ciw6x5">
                <span className="bg-white w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 border border-offwhite-200" data-id="element-174" data-mp="3nc5zwubro263">
                  <MapPinIcon size={20} className="text-amber-500" aria-hidden="true" data-id="element-175" data-mp="17jglabl8eowl" />
                </span>
                <span className="text-charcoal-800" data-id="element-176" data-mp="2kia2esc49txe">
                  House of Gains Gym, Camarillo, CA
                  <span className="block text-sm text-charcoal-500" data-id="element-177" data-mp="2pk7qq0zdjex7">
                    In-person visits by appointment only
                  </span>
                </span>
              </li>
            </ul>
          </motion.div>

          {/* Form card */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.4,
          delay: 0.08,
          ease: [0.23, 1, 0.32, 1]
        }} className="bg-white rounded-3xl border border-offwhite-200 shadow-lg p-8 md:p-10" data-id="element-178" data-mp="347kelwcnlwqr">
            {status === 'success' ? <div className="text-center py-10" role="status" data-id="element-179" data-mp="5nzm44sbdmwd">
                <span className="inline-flex w-16 h-16 rounded-full bg-amber-500/15 items-center justify-center mb-6" data-id="element-180" data-mp="5n7x5ct82nf2">
                  <CheckCircle2Icon size={32} className="text-amber-600" aria-hidden="true" data-id="element-181" data-mp="n23jr3e6quc" />
                </span>
                <h3 className="text-2xl font-bold text-charcoal-950 mb-3" data-id="element-182" data-mp="3myh4dzt0hxxh">
                  Message sent
                </h3>
                <p className="text-charcoal-800 leading-relaxed max-w-md mx-auto mb-8" data-id="element-183" data-mp="3e42obv0nqjdb">
                  Thanks for reaching out. I read every message personally and
                  typically respond within one business day.
                </p>
                <button type="button" onClick={() => setStatus('idle')} className="bg-charcoal-950 hover:bg-charcoal-800 text-white px-7 py-3 rounded-full font-semibold transition-colors duration-150" data-id="element-184" data-mp="3476hdtqeq5av">
                  Send another message
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate data-id="element-185" data-mp="1engcs7yto6q9">
                <div className="grid sm:grid-cols-2 gap-5 mb-5" data-id="element-186" data-mp="1jnkug1bgv7i2">
                  <Field id="contact-name" label="Name" required error={errors.name} data-id="element-187" data-mp="1jo40okrr87g9">
                    <input id="contact-name" name="name" type="text" autoComplete="name" value={fields.name} onChange={e => handleChange('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'contact-name-error' : undefined} className={inputClass(Boolean(errors.name))} placeholder="Jane Doe" data-id="element-188" data-mp="r085jkhu2nll" />
                  </Field>

                  <Field id="contact-phone" label="Phone" hint="Optional" error={errors.phone} data-id="element-189" data-mp="2xqhbiuk5eqi7">
                    <input id="contact-phone" name="phone" type="tel" autoComplete="tel" value={fields.phone} onChange={e => handleChange('phone', e.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'contact-phone-error' : undefined} className={inputClass(Boolean(errors.phone))} placeholder="(805) 555-0142" data-id="element-190" data-mp="36r5nljpzegsg" />
                  </Field>
                </div>

                <div className="mb-5" data-id="element-191" data-mp="38cg9onsqo6q">
                  <Field id="contact-email" label="Email" required error={errors.email} data-id="element-192" data-mp="mvpfr1o98sjh">
                    <input id="contact-email" name="email" type="email" autoComplete="email" value={fields.email} onChange={e => handleChange('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'contact-email-error' : undefined} className={inputClass(Boolean(errors.email))} placeholder="you@example.com" data-id="element-193" data-mp="cyb6bm0r140o" />
                  </Field>
                </div>

                <div className="mb-6" data-id="element-194" data-mp="3lduw2b27o4az">
                  <Field id="contact-message" label="How can I help?" required error={errors.message} data-id="element-195" data-mp="3lctahcnkjzf0">
                    <textarea id="contact-message" name="message" rows={6} value={fields.message} onChange={e => handleChange('message', e.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'contact-message-error' : undefined} className={`${inputClass(Boolean(errors.message))} resize-y min-h-[140px]`} placeholder="Tell me what's going on — where it hurts, how long it's been bothering you, what you've already tried, and what you're hoping to get back to." data-id="element-196" data-mp="3npz1kxik65n3" />
                  </Field>
                </div>

                {status === 'error' && <div role="alert" className="flex gap-3 rounded-xl border border-red-300 bg-red-50 p-4 mb-6" data-id="element-197" data-mp="2dr68qbyvfm64">
                    <AlertCircleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-198" data-mp="28q0iqlwlid0a" />
                    <p className="text-sm text-red-800" data-id="element-199" data-mp="2ijzgl8pmzamf">
                      Something went wrong sending your message. Please try
                      again, or email{' '}
                      <a href="mailto:info@mcleanphysicaltherapy.com" className="font-semibold underline" data-id="element-200" data-mp="1pus7jfsk32mx">
                        info@mcleanphysicaltherapy.com
                      </a>
                      .
                    </p>
                  </div>}

                <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/60 disabled:cursor-not-allowed text-charcoal-950 px-8 py-4 rounded-full font-bold text-lg transition-colors duration-150" data-id="element-201" data-mp="3b6k4u0atf6lr">
                  {isSubmitting ? <>
                      <LoaderIcon size={20} className="animate-spin" aria-hidden="true" data-id="element-202" data-mp="10lj4hs9f7ax" />
                      Sending…
                    </> : <>
                      Send Message
                      <SendIcon size={18} aria-hidden="true" data-id="element-203" data-mp="39g5blel3wlox" />
                    </>}
                </button>

                <p className="text-sm text-charcoal-500 text-center mt-4" data-id="element-204" data-mp="2gsso9274g9uk">
                  I typically respond within one business day.
                </p>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}
type FieldProps = {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
};
function Field({
  id,
  label,
  required,
  hint,
  error,
  children
}: FieldProps) {
  return <div data-id="element-205" data-mp="ty5xpaxtdrg7">
      <div className="flex items-baseline justify-between gap-2 mb-2" data-id="element-206" data-mp="txw5dgpar87c">
        <label htmlFor={id} className="block text-sm font-bold text-charcoal-950" data-id="element-207" data-mp="owgj5lbduvnd">
          {label}
          {required && <span className="text-amber-600" aria-hidden="true" data-id="element-208" data-mp="f6n7kkb8qgj4">
              {' '}
              *
            </span>}
        </label>
        {hint && <span className="text-xs text-charcoal-500" data-id="element-209" data-mp="b3h5zpz2pdiz">{hint}</span>}
      </div>
      {children}
      {error && <p id={`${id}-error`} role="alert" className="mt-2 text-sm font-medium text-red-600" data-id="element-210" data-mp="64ns3wfvjkia">
          {error}
        </p>}
    </div>;
}
function inputClass(hasError: boolean) {
  return ['w-full rounded-xl border bg-white px-4 py-3 text-charcoal-950', 'placeholder:text-charcoal-500/50', 'focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-150', hasError ? 'border-red-400 focus:border-red-500 focus:ring-red-200' : 'border-offwhite-200 focus:border-amber-500 focus:ring-amber-500/30'].join(' ');
}
function validate(fields: FormFields): FormErrors {
  const errors: FormErrors = {};
  if (!fields.name.trim()) {
    errors.name = 'Please enter your name.';
  }
  if (!fields.email.trim()) {
    errors.email = 'Please enter your email so I can reply.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }
  if (fields.phone.trim() && fields.phone.replace(/\D/g, '').length < 10) {
    errors.phone = 'Please enter a valid phone number.';
  }
  if (!fields.message.trim()) {
    errors.message = 'Please add a short message.';
  } else if (fields.message.trim().length < 10) {
    errors.message = 'Please add a little more detail.';
  }
  return errors;
}